import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Trophy, ChevronRight, FileDown, MessageCircle, Clock, Upload, Loader2, CalendarDays } from "lucide-react";
import { phaseBadge, phaseLabel } from "@/lib/seasonPhase";
import { toast } from "sonner";
import { exportMatchPdf } from "@/lib/export";

export default function MatchesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selTeam, setSelTeam] = useState("");
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const qs = new URLSearchParams(window.location.search);
    return { from: qs.get("date_from") || "", to: qs.get("date_to") || "" };
  });
  const [filterRival, setFilterRival] = useState("all");
  const [filterJornada, setFilterJornada] = useState("all");
  const [form, setForm] = useState({ date: "", rival: "", home_away: "casa", result: "", location: "", time: "", kickoff_time: "", kickoff_location: "", kickoff_url: "", season_phase: "" });
  const calRef = useRef(null);
  const [calFile, setCalFile] = useState(null);
  const [calInfo, setCalInfo] = useState(null);
  const [calClub, setCalClub] = useState("");
  const [calBusy, setCalBusy] = useState(false);

  useEffect(() => {
    api.get("/teams").then((r) => {
      setTeams(r.data);
      const qp = new URLSearchParams(window.location.search).get("team_id");
      const saved = localStorage.getItem("app_team_filter");
      if (r.data.length) {
        setSelTeam((s) => {
          if (s) return s;
          if (qp && r.data.some((t) => t.id === qp)) return qp;
          if (saved && saved !== "__all__" && r.data.some((t) => t.id === saved)) return saved;
          return r.data[0].id;
        });
      }
    });
  }, []);

  const pickTeam = (v) => {
    setSelTeam(v);
    localStorage.setItem("app_team_filter", v);
  };

  const load = () => {
    if (!selTeam) return;
    api.get(`/matches?team_id=${selTeam}`).then((r) => setMatches(r.data));
  };
  useEffect(() => { load(); }, [selTeam]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/matches", { team_id: selTeam, ...form, season_phase: form.season_phase || null });
      toast.success("Partido creado");
      setForm({ date: "", rival: "", home_away: "casa", result: "", location: "", time: "", kickoff_time: "", kickoff_location: "", kickoff_url: "", season_phase: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar partido vs ${m.rival}?`)) return;
    await api.delete(`/matches/${m.id}`);
    toast.success("Eliminado");
    load();
  };

  const shareWhatsApp = async (m) => {
    try {
      const { data } = await api.get(`/matches/${m.id}/convocation`);
      const pl = await api.get(`/players?team_id=${m.team_id}`);
      const byId = Object.fromEntries(pl.data.map((p) => [p.id, p]));
      const CALLED = new Set(["titular", "suplente", "convocado"]);
      const sortByDorsal = (a, b) => (a.dorsal || 99) - (b.dorsal || 99);
      const fmt = (p) => `• ${p.dorsal ? `#${p.dorsal} ` : ""}${p.name}`;

      const items = data.items || [];
      const calledList = items
        .filter((c) => CALLED.has(c.status))
        .map((c) => byId[c.player_id])
        .filter(Boolean)
        .sort(sortByDorsal);
      const notCalledList = items
        .filter((c) => c.status && !CALLED.has(c.status))
        .map((c) => byId[c.player_id])
        .filter(Boolean)
        .sort(sortByDorsal);
      const called = calledList.map(fmt).join("\n");
      const notCalled = notCalledList.map(fmt).join("\n");

      const where = m.home_away === "casa" ? "En casa" : "Fuera";
      const lines = [
        `*Convocatoria ${teamName ? `${teamName} ` : ""}— vs ${m.rival}*`,
        `Fecha del partido: ${m.date}`,
        where,
      ];
      if (m.time) lines.push(`Hora de convocatoria: ${m.time}`);
      if (m.location) lines.push(`Lugar de convocatoria: ${m.location}`);
      if (m.kickoff_time) lines.push(`Hora del partido: ${m.kickoff_time}`);
      if (m.kickoff_location) lines.push(`Lugar del partido: ${m.kickoff_location}`);
      if (m.kickoff_url) lines.push(`📍 Cómo llegar al campo:\n${m.kickoff_url}`);
      const text =
        lines.join("\n") + "\n\n" +
        `*Convocados (${calledList.length}):*\n${called || "—"}\n\n` +
        `*No convocados (${notCalledList.length}):*\n${notCalled || "—"}\n\n` +
        `¡Nos vemos!`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    } catch {
      toast.error("No se pudo preparar el mensaje");
    }
  };

  const canEdit = user?.role === "admin" || (user?.team_ids || []).includes(selTeam);

  const onCalendarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !selTeam) return;
    setCalBusy(true);
    try {
      const fd = new FormData();
      fd.append("team_id", selTeam);
      fd.append("file", file);
      const { data } = await api.post("/matches/import-calendar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCalFile(file);
      setCalInfo(data);
      setCalClub(data.detected || "");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCalBusy(false);
    }
  };

  const confirmCalendarImport = async () => {
    if (!calFile || !calClub) return;
    setCalBusy(true);
    try {
      const fd = new FormData();
      fd.append("team_id", selTeam);
      fd.append("club_name", calClub);
      fd.append("file", calFile);
      const { data } = await api.post("/matches/import-calendar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Calendario importado: ${data.created} partidos creados${data.skipped ? `, ${data.skipped} ya existían` : ""}`);
      setCalInfo(null);
      setCalFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCalBusy(false);
    }
  };
  const teamName = teams.find((t) => t.id === selTeam)?.name || "";
  const rivalOptions = Array.from(new Set(matches.map((m) => m.rival).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const jornadaOptions = Array.from(new Set(matches.map((m) => m.jornada).filter((j) => j != null))).sort((a, b) => a - b);
  const hasFilter = Boolean(dateRange.from || dateRange.to || filterRival !== "all" || filterJornada !== "all");
  const visibleMatches = matches.filter((m) => {
    const d = String(m.date).slice(0, 10);
    if (dateRange.from && d < dateRange.from) return false;
    if (dateRange.to && d > dateRange.to) return false;
    if (filterRival !== "all" && m.rival !== filterRival) return false;
    if (filterJornada !== "all" && String(m.jornada) !== String(filterJornada)) return false;
    return true;
  });
  const clearFilters = () => { setDateRange({ from: "", to: "" }); setFilterRival("all"); setFilterJornada("all"); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "Nunito" }}>Partidos</h1>
          <p className="text-slate-500 text-sm mt-1">
            Convocatorias y minutos jugados.
            {teamName && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs font-bold" data-testid="matches-team-name">
                {teamName}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {teams.length > 0 && (
            <Select value={selTeam} onValueChange={pickTeam}>
              <SelectTrigger className="w-56" data-testid="matches-team-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canEdit && selTeam && (
            <>
              <input
                ref={calRef}
                type="file"
                className="hidden"
                accept=".pdf,application/pdf"
                onChange={onCalendarFile}
                data-testid="import-calendar-input"
              />
              <Button
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold"
                onClick={() => calRef.current?.click()}
                disabled={calBusy}
                data-testid="import-calendar-button"
              >
                {calBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Importar calendario
              </Button>
            </>
          )}
          {canEdit && selTeam && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white font-bold" data-testid="new-match-button">
                  <Plus className="h-4 w-4 mr-2" /> Nuevo partido
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuevo partido</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Fecha</Label>
                      <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="match-date-input" />
                    </div>
                    <div>
                      <Label>Hora de convocatoria <span className="text-red-500">*</span></Label>
                      <Input type="time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="HH:MM" data-testid="match-time-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Lugar de convocatoria</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ej. Vestuarios del campo a las..." data-testid="match-location-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Hora del partido <span className="text-red-500">*</span></Label>
                      <Input type="time" required value={form.kickoff_time} onChange={(e) => setForm({ ...form, kickoff_time: e.target.value })} placeholder="HH:MM" data-testid="match-kickoff-time-input" />
                    </div>
                    <div>
                      <Label>Lugar del partido</Label>
                      <Input value={form.kickoff_location} onChange={(e) => setForm({ ...form, kickoff_location: e.target.value })} placeholder="Ej. Campo Concepción 1" data-testid="match-kickoff-location-input" />
                    </div>
                  </div>
                  <div>
                    <Label>URL del campo (Google Maps, etc.)</Label>
                    <Input type="url" value={form.kickoff_url} onChange={(e) => setForm({ ...form, kickoff_url: e.target.value })} placeholder="https://maps.google.com/?q=..." data-testid="match-kickoff-url-input" />
                  </div>
                  <div>
                    <Label>Rival</Label>
                    <Input required value={form.rival} onChange={(e) => setForm({ ...form, rival: e.target.value })} data-testid="match-rival-input" />
                  </div>
                  <div>
                    <Label>Localización</Label>
                    <Select value={form.home_away} onValueChange={(v) => setForm({ ...form, home_away: v })}>
                      <SelectTrigger data-testid="match-location-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="casa">Casa</SelectItem>
                        <SelectItem value="fuera">Fuera</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Resultado (opcional)</Label>
                    <Input value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} placeholder="Ej. 3-1" data-testid="match-result-input" />
                  </div>
                  <div>
                    <Label>Fase</Label>
                    <Select value={form.season_phase || "auto"} onValueChange={(v) => setForm({ ...form, season_phase: v === "auto" ? "" : v })}>
                      <SelectTrigger data-testid="match-phase-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Automática (según fechas del equipo)</SelectItem>
                        <SelectItem value="PRESEASON">Pretemporada</SelectItem>
                        <SelectItem value="COMPETITION">Competición</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter>
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="match-save-button">Crear</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {calInfo && (
        <Dialog open onOpenChange={(v) => { if (!v) { setCalInfo(null); setCalFile(null); } }}>
          <DialogContent data-testid="import-calendar-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-purple-600" /> Importar calendario de competición
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 text-sm text-slate-700">
                <div><b>{calInfo.jornadas}</b> jornadas · <b>{calInfo.participants.length}</b> equipos participantes</div>
                <div className="text-xs text-slate-500 mt-0.5">{calInfo.date_from} → {calInfo.date_to}</div>
              </div>
              <div>
                <Label>¿Cuál es tu equipo en este calendario?</Label>
                <Select value={calClub} onValueChange={setCalClub}>
                  <SelectTrigger className="mt-1" data-testid="import-calendar-club-select">
                    <SelectValue placeholder="Elegir equipo…" />
                  </SelectTrigger>
                  <SelectContent>
                    {calInfo.participants.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {calInfo.detected && (
                  <p className="text-[11px] text-emerald-700 mt-1">Detectado automáticamente: {calInfo.detected}</p>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Se crearán los partidos de liga de <b>{teamName}</b> con fecha, rival y casa/fuera. Los que ya existan (misma fecha y rival) no se duplicarán.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCalInfo(null); setCalFile(null); }} data-testid="import-calendar-cancel">Cancelar</Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                onClick={confirmCalendarImport}
                disabled={!calClub || calBusy}
                data-testid="import-calendar-confirm"
              >
                {calBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Crear partidos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {selTeam && (
        <div className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3" data-testid="matches-filter-bar">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Rival</label>
            <Select value={filterRival} onValueChange={setFilterRival}>
              <SelectTrigger className="w-52" data-testid="matches-filter-rival"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los rivales</SelectItem>
                {rivalOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Jornada</label>
            <Select value={filterJornada} onValueChange={setFilterJornada}>
              <SelectTrigger className="w-36" data-testid="matches-filter-jornada"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {jornadaOptions.map((j) => <SelectItem key={j} value={String(j)}>Jornada {j}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Desde</label>
            <Input type="date" value={dateRange.from} onChange={(e) => setDateRange((r) => ({ ...r, from: e.target.value }))} className="w-40" data-testid="matches-filter-date-from" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Hasta</label>
            <Input type="date" value={dateRange.to} min={dateRange.from || undefined} onChange={(e) => setDateRange((r) => ({ ...r, to: e.target.value }))} className="w-40" data-testid="matches-filter-date-to" />
          </div>
          {hasFilter && (
            <Button variant="ghost" className="text-purple-700 font-bold" onClick={clearFilters} data-testid="matches-filter-clear">
              ✕ Limpiar filtros
            </Button>
          )}
          <span className="ml-auto text-xs text-slate-500 font-semibold" data-testid="matches-filter-count">
            {visibleMatches.length} de {matches.length} partidos
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleMatches.length === 0 && (
          <div className="text-slate-500 text-sm col-span-full">
            {hasFilter ? "No hay partidos que coincidan con los filtros." : "Aún no hay partidos."}
          </div>
        )}
        {visibleMatches.map((m) => (
          <Card key={m.id} className="border-slate-200 shadow-sm hover:-translate-y-1 transition-transform cursor-pointer" onClick={() => navigate(`/matches/${m.id}`)} data-testid={`match-card-${m.id}`}>
            <CardContent className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">
                <Trophy className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${m.home_away === "casa" ? "bg-purple-100 text-purple-800" : "bg-sky-100 text-sky-700"}`}>
                    {m.home_away}
                  </span>
                  {m.jornada && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold bg-amber-100 text-amber-800" data-testid={`match-jornada-badge-${m.id}`}>
                      J{m.jornada}
                    </span>
                  )}
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${phaseBadge(m.season_phase)}`} data-testid={`match-phase-badge-${m.id}`}>
                    {phaseLabel(m.season_phase)}
                  </span>
                  {m.result && <span className="text-xs font-bold text-slate-700">{m.result}</span>}
                </div>
                <div className="text-lg font-black mt-1" style={{ fontFamily: "Nunito" }}>vs {m.rival}</div>
                {teamName && <div className="text-xs font-bold text-purple-700">{teamName}</div>}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span>{m.date}</span>
                  {m.kickoff_time && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full"
                      data-testid={`match-kickoff-badge-${m.id}`}
                      title={m.kickoff_location ? `Hora del partido — ${m.kickoff_location}` : "Hora del partido"}
                    >
                      <Clock className="h-3 w-3" /> {m.kickoff_time}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); shareWhatsApp(m); }} data-testid={`whatsapp-match-${m.id}`} title="Compartir por WhatsApp">
                  <MessageCircle className="h-4 w-4 text-[#128C7E]" />
                </Button>
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); exportMatchPdf(m); }} data-testid={`export-match-${m.id}`} title="Descargar convocatoria">
                  <FileDown className="h-4 w-4" />
                </Button>
                {canEdit && (
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); remove(m); }} data-testid={`delete-match-${m.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
